import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/auth-store';
import { authService } from '../services/auth-service';

export function Login() {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authService.login({ email, password });

      // Check if user has Superadmin role
      if (!response.user?.roles?.includes('Superadmin')) {
        setError('Access denied. Only Superadmin users can access the admin panel.');
        setIsLoading(false);
        return;
      }

      login(response.token, response.user);
      navigate('/');
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Invalid email or password');
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-surface-app"
      style={{
        backgroundImage: 'url(/login-bg.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Brand-tinted overlay — bridges to Spotto landing/Auth dark theme */}
      <div className="absolute inset-0 bg-gradient-to-br from-surface-app/88 via-brand-primary-deep/60 to-brand-primary/35" />

      <div className="relative max-w-md w-full bg-surface-app-elevated backdrop-blur-md shadow-2xl rounded-xl p-8 border border-white/10">
        <div className="mb-8 text-center">
          <h1 className="font-display text-3xl font-light tracking-tight text-ink-primary">Spotto Admin</h1>
          <p className="text-ink-secondary mt-2">Platform Administration</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-brand-warm/20 border border-brand-warm/40 text-brand-warm px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-secondary mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent placeholder-ink-muted shadow-sm"
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-ink-secondary mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-2 bg-surface-input border border-white/15 text-ink-primary rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent placeholder-ink-muted shadow-sm"
              placeholder="••••••••"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-brand-primary text-white py-2.5 px-4 rounded-lg hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 focus:ring-offset-surface-app-elevated disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors shadow-md"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-ink-muted">
          <p>Superadmin access only</p>
        </div>
      </div>
    </div>
  );
}
